import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_LOCALE, PREFIXED_LOCALES, LOCALES } from "@/i18n/locales";
import { translations } from "@/i18n/translations";
import { setApiLocale } from "@/lib/api";

const LanguageContext = createContext(null);
const STORAGE_KEY = "mlv_locale";

function localeFromPath(pathname) {
  const seg = pathname.split("/")[1];
  return PREFIXED_LOCALES.includes(seg) ? seg : DEFAULT_LOCALE;
}

function stripLocale(pathname) {
  const parts = pathname.split("/");
  if (PREFIXED_LOCALES.includes(parts[1])) {
    parts.splice(1, 1);
    const rebuilt = parts.join("/");
    return rebuilt === "" ? "/" : rebuilt;
  }
  return pathname;
}

function buildLocalePath(path, locale) {
  const clean = path.startsWith("/") ? path : `/${path}`;
  if (locale === DEFAULT_LOCALE) return clean;
  return clean === "/" ? `/${locale}` : `/${locale}${clean}`;
}

function getPath(obj, key) {
  return key.split(".").reduce((o, k) => (o && o[k] != null ? o[k] : undefined), obj);
}

function interpolate(str, vars) {
  if (!vars) return str;
  return str.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const locale = localeFromPath(location.pathname);
  // Keep the API client in sync synchronously (before child data fetches run).
  setApiLocale(locale);
  const didInit = useRef(false);

  // Persist the active locale so it is remembered on the next visit.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch (e) {
      /* ignore */
    }
  }, [locale]);

  // First-visit auto-detection (root only): remembered choice > browser language.
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    if (location.pathname !== "/") return; // only redirect from the bare root
    let stored = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch (e) {
      stored = null;
    }
    let target = null;
    if (stored && PREFIXED_LOCALES.includes(stored)) {
      target = stored;
    } else if (!stored) {
      const raw = (navigator.language || "en").slice(0, 2).toLowerCase();
      const mapped = raw === "ua" ? "uk" : raw;
      if (PREFIXED_LOCALES.includes(mapped)) target = mapped;
    }
    if (target) navigate(buildLocalePath("/", target), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useCallback(
    (key, vars) => {
      const val = getPath(translations[locale], key) ?? getPath(translations[DEFAULT_LOCALE], key) ?? key;
      return typeof val === "string" ? interpolate(val, vars) : val;
    },
    [locale]
  );

  const localePath = useCallback((path) => buildLocalePath(path, locale), [locale]);

  const setLocale = useCallback(
    (code) => {
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch (e) {
        /* ignore */
      }
      const base = stripLocale(location.pathname);
      const target = buildLocalePath(base, code) + location.search;
      navigate(target);
    },
    [location.pathname, location.search, navigate]
  );

  const value = useMemo(
    () => ({ locale, t, localePath, setLocale, locales: LOCALES, defaultLocale: DEFAULT_LOCALE }),
    [locale, t, localePath, setLocale]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (ctx) return ctx;
  // Safe fallback if used outside provider (e.g. during isolated tests).
  return {
    locale: DEFAULT_LOCALE,
    t: (key) => getPath(translations[DEFAULT_LOCALE], key) ?? key,
    localePath: (p) => p,
    setLocale: () => {},
    locales: LOCALES,
    defaultLocale: DEFAULT_LOCALE,
  };
};

export { stripLocale, buildLocalePath };
